// src/cases/ConfidisCaseList.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { buildAlphaNumSearchToken } from './cofidisSearchTokens';
import { exportarCasos } from './exportarCasos';
import { contarCasosDelEspejo, leerCasos } from './casosDelEspejo';
import {
  ListContextProvider,
  useListContext,
  CreateButton,
  ExportButton,
} from "react-admin";
import { Box, CircularProgress, Button, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { CofidisCaseFilter } from './CofidisCaseFilter';
import { getFirestore, collection, query, orderBy, where, limit, startAfter, getCountFromServer, getDocsFromCache, getDocsFromServer, doc, updateDoc } from 'firebase/firestore';
import { formatDateForDisplay } from '../utils/dateUtils';
import { agentOptionsForValue, normalizeAgentValue } from './cofidisAgentConfig';
import {
  COUNT_CACHE_MAX_AGE_MS,
  LIST_CACHE_MAX_AGE_MS,
  getFreshSessionValue,
  isSessionValueFresh,
  loadQueryWithCache,
  markSessionValueFresh,
  setSessionValue,
} from '../utils/firestoreCache';

// Custom field to display nested data
const NestedTextField = ({ source, label, record }) => {
  if (!record) return <span style={{ color: 'var(--ed-text-3, #6c757d)', fontStyle: 'italic' }}>-</span>;

  const keys = source.split('.');
  let value = record;
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      value = '';
      break;
    }
  }
  return <span style={{ fontWeight: 500, color: 'var(--ed-text, #212529)' }}>{value || '-'}</span>;
};

// Custom field to display first holder name
const FirstHolderNameField = ({ record }) => {
  if (!record) return <span style={{ color: 'var(--ed-text-3, #6c757d)', fontStyle: 'italic' }}>-</span>;

  const primerTitular = record?.primerTitular;
  if (!primerTitular) return <span style={{ color: 'var(--ed-text-3, #6c757d)', fontStyle: 'italic' }}>-</span>;

  const nombre = primerTitular.nombre || '';
  const apellidos = primerTitular.apellidos || '';
  const fullName = `${nombre} ${apellidos}`.trim();

  return (
    <span style={{
      fontWeight: 500,
      color: 'var(--ed-text, #212529)',
      display: 'block',
      lineHeight: '1.4'
    }}>
      {fullName || '-'}
    </span>
  );
};

// Custom field to display second holder name
const SecondHolderNameField = ({ record }) => {
  if (!record) return <span style={{ color: 'var(--ed-text-3, #6c757d)', fontStyle: 'italic' }}>-</span>;

  const segundoTitular = record?.segundoTitular;
  if (!segundoTitular || !segundoTitular.nombre) return <span style={{ color: 'var(--ed-text-3, #6c757d)', fontStyle: 'italic' }}>-</span>;

  const nombre = segundoTitular.nombre || '';
  const apellidos = segundoTitular.apellidos || '';
  const fullName = `${nombre} ${apellidos}`.trim();

  return (
    <span style={{
      fontWeight: 500,
      color: 'var(--ed-text, #212529)',
      display: 'block',
      lineHeight: '1.4'
    }}>
      {fullName || '-'}
    </span>
  );
};

// Inline editable cell for agent name fields
// eslint-disable-next-line no-unused-vars
const InlineEditCell = ({ record, field, caseId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(record?.[field] || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'cofidisCases', caseId), { [field]: value });
    } catch (err) {
      console.error('Error saving agent:', err);
    }
    setSaving(false);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setValue(record?.[field] || '');
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        disabled={saving}
        style={{
          border: '1px solid #2196f3',
          borderRadius: '4px',
          padding: '4px 8px',
          fontSize: '0.875rem',
          width: '140px',
          outline: 'none',
        }}
      />
    );
  }

  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        setIsEditing(true);
      }}
      title="Clic para editar"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        cursor: 'pointer',
        color: value ? 'var(--ed-text, #212529)' : 'var(--ed-text-4, #adb5bd)',
        fontStyle: value ? 'normal' : 'italic',
        minWidth: '80px',
      }}
    >
      {saving ? '...' : (value || '— Asignar —')}
      {!saving && <span style={{ color: '#2196f3', fontSize: '0.75rem', opacity: 0.7 }}>✏</span>}
    </span>
  );
};

const getPrimaryPhoneValue = (record) => (
  record?.primerTitular?.telefono ||
  record?.primerTitular?.movil ||
  record?.primerTitular?.telefonoFijo ||
  record?.phone ||
  record?.mobile ||
  record?.movil ||
  '-'
);

const PhoneField = ({ record }) => (
  <span style={{ fontWeight: 500, color: 'var(--ed-text, #212529)' }}>
    {getPrimaryPhoneValue(record)}
  </span>
);

const InlineSelectCell = ({ record, field, caseId }) => {
  const rawValue = record?.[field] || '';
  const normalizedRecordValue = normalizeAgentValue(field, record?.[field]);
  const [value, setValue] = useState(normalizedRecordValue);
  const [saving, setSaving] = useState(false);
  const lastNormalizedRawValueRef = useRef(null);

  useEffect(() => {
    setValue(normalizedRecordValue);
  }, [normalizedRecordValue]);

  useEffect(() => {
    if (!rawValue || rawValue === normalizedRecordValue || lastNormalizedRawValueRef.current === rawValue) {
      return;
    }

    let isMounted = true;
    lastNormalizedRawValueRef.current = rawValue;

    const syncLegacyValue = async () => {
      setSaving(true);

      try {
        const db = getFirestore();
        await updateDoc(doc(db, 'cofidisCases', caseId), { [field]: normalizedRecordValue });
      } catch (err) {
        console.error('Error normalizing agent selection:', err);
        if (isMounted) {
          lastNormalizedRawValueRef.current = null;
        }
      } finally {
        if (isMounted) {
          setSaving(false);
        }
      }
    };

    syncLegacyValue();

    return () => {
      isMounted = false;
    };
  }, [caseId, field, normalizedRecordValue, rawValue]);

  const handleChange = async (event) => {
    const nextValue = normalizeAgentValue(field, event.target.value);
    const previousValue = value;

    setValue(nextValue);
    setSaving(true);
    lastNormalizedRawValueRef.current = nextValue;

    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'cofidisCases', caseId), { [field]: nextValue });
    } catch (err) {
      console.error('Error saving agent selection:', err);
      lastNormalizedRawValueRef.current = null;
      setValue(previousValue);
    } finally {
      setSaving(false);
    }
  };

  return (
    <select
      value={value}
      onChange={handleChange}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onFocus={(event) => event.stopPropagation()}
      disabled={saving}
      style={{
        width: '160px',
        border: '1px solid var(--ed-border, #ced4da)',
        borderRadius: '6px',
        padding: '6px 28px 6px 10px',
        fontSize: '0.875rem',
        color: 'var(--ed-text, #212529)',
        backgroundColor: saving ? 'var(--ed-soft, #f8f9fa)' : 'var(--ed-card, #ffffff)',
        cursor: saving ? 'wait' : 'pointer',
        outline: 'none',
      }}
    >
      {agentOptionsForValue(field, rawValue).map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
};



// React-Admin defaults to a hash-based router; detect at call time so the
// "Abrir" button generates a URL the browser can open in a new tab.
const buildShowUrl = (id) => {
  if (typeof window === 'undefined' || !id) {
    return `#/cofidisCases/${id}/show`;
  }

  const usesHashRouter = window.location.hash.startsWith('#/');
  const path = `/cofidisCases/${id}/show`;

  return usesHashRouter
    ? `${window.location.pathname}${window.location.search}#${path}`
    : path;
};

// Memoized filter component
const MemoCofidisCaseFilter = React.memo(CofidisCaseFilter);
const SEARCH_WINDOW_MIN = 200;
const SEARCH_PREFIX_MIN = 3;
const SEARCH_PREFIX_MAX = 10;
const COFIDIS_COUNT_CACHE_PREFIX = 'cofidisCases:count:';
const COFIDIS_LIST_CACHE_PREFIX = 'cofidisCases:list:';
const COFIDIS_SEARCH_BATCH_CACHE_PREFIX = 'cofidisCases:searchBatch:';

const createEmptySearchCacheState = () => ({
  key: null,
  cases: [],
  nextCursor: null,
  hasMoreServer: true,
});

const mergeCasesById = (existingCases, nextCases) => {
  if (!existingCases.length) {
    return nextCases;
  }

  const seenIds = new Set(existingCases.map((caseData) => caseData.id));
  const mergedCases = [...existingCases];

  nextCases.forEach((caseData) => {
    if (!seenIds.has(caseData.id)) {
      seenIds.add(caseData.id);
      mergedCases.push(caseData);
    }
  });

  return mergedCases;
};

const normalizeCofidisFilters = (filters = {}) => ({
  q: (filters.q || '').trim(),
  mobile: (filters.mobile || '').trim(),
  bankGroup: filters.bankGroup || '',
  operacionesAgent: filters.operacionesAgent
    ? normalizeAgentValue('operacionesAgent', filters.operacionesAgent)
    : '',
  comercialAgent: filters.comercialAgent
    ? normalizeAgentValue('comercialAgent', filters.comercialAgent)
    : '',
});

const hasTextSearchFilters = (filters = {}) => Boolean(filters.q || filters.mobile);

const normalizeSearchText = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const normalizeDigits = (value = '') => String(value).replace(/\D/g, '');

// El buscador del móvil tiene que dar igual cómo se escriba el número (André,
// 28/08): "+34 645 47 58 36", "0034645475836" y "645475836" son el mismo
// teléfono. Misma regla que en ANDI: fuera el 00 y fuera el 34 cuando sobra.
const phoneKey = (value = '') => {
  const d = normalizeDigits(value).replace(/^00/, '');
  return d.length > 9 && d.startsWith('34') ? d.slice(2) : d;
};

// Los casos están guardados casi siempre en formato nacional, pero alguno
// llegó con el +34 delante, así que se pregunta por las dos formas a la vez.
const buildMobileTokens = (value = '') => {
  const nacional = phoneKey(value);
  if (!nacional || nacional.length < SEARCH_PREFIX_MIN) {
    return [];
  }
  const recorta = (num) => `mobile:${num.slice(0, Math.min(num.length, SEARCH_PREFIX_MAX))}`;
  return Array.from(new Set([recorta(nacional), recorta(`34${nacional}`)]));
};

// Mirror the cloud function tokenizer in functions/src/searchIndexHelpers.js so
// the array-contains lookup hits the same prefixes the indexer wrote.
const buildSearchToken = (tag, value, normalizer) => {
  const normalized = normalizer(value);
  if (!normalized || normalized.length < SEARCH_PREFIX_MIN) {
    return null;
  }

  const cleaned = normalized
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const firstSegment = cleaned.split(' ').filter(Boolean)[0];
  if (!firstSegment || firstSegment.length < SEARCH_PREFIX_MIN) {
    return null;
  }

  const length = Math.min(firstSegment.length, SEARCH_PREFIX_MAX);
  return `${tag}:${firstSegment.slice(0, length)}`;
};

// El indexador guarda DNI y numero de caso en MAYUSCULAS y sin separadores
// (normalizeAlphaNum, functions/src/searchIndexHelpers.js:19), mientras que los
// nombres van en minusculas (normalizeText). Aqui solo se generaba la forma en
// minusculas, asi que buscar un DNI o un NIE no encontraba NUNCA nada y la
// pantalla lo presentaba como «no existe el caso», que es justo lo que lleva a
// crear un duplicado. Se arregla en el lector: se consultan las dos formas del
// token y no hay que reindexar nada.
//
const getPrimarySearchTokens = (filters = {}) => {
  const mobileTokens = buildMobileTokens(filters.mobile);
  if (mobileTokens.length) {
    return mobileTokens;
  }
  // Un nombre casa con la forma en minusculas y un DNI con la de mayusculas;
  // la que no corresponde simplemente no existe en el indice, asi que buscar
  // por las dos no puede traer resultados de mas.
  const tokens = [
    buildSearchToken('q', filters.q, normalizeSearchText),
    buildAlphaNumSearchToken('q', filters.q),
  ].filter(Boolean);
  return Array.from(new Set(tokens));
};

const buildCofidisCountCacheKey = (filters = {}) =>
  `${COFIDIS_COUNT_CACHE_PREFIX}${JSON.stringify({
    bankGroup: filters.bankGroup || '',
    operacionesAgent: filters.operacionesAgent || '',
    comercialAgent: filters.comercialAgent || '',
  })}`;

const buildCofidisListCacheKey = ({ filters, page, perPage }) =>
  `${COFIDIS_LIST_CACHE_PREFIX}${JSON.stringify({
    filters,
    page,
    perPage,
  })}`;

const buildCofidisSearchBatchCacheKey = ({ filters, cursorId, windowSize, token }) =>
  `${COFIDIS_SEARCH_BATCH_CACHE_PREFIX}${JSON.stringify({
    filters,
    cursorId: cursorId || 'start',
    windowSize,
    token: token || '',
  })}`;

const filterCofidisCases = (cases, currentFilters) => {
  let filteredCases = cases;

  if (currentFilters.q) {
    const searchTerm = normalizeSearchText(currentFilters.q);
    filteredCases = filteredCases.filter((caseData) => {
      const primerTitular = caseData.primerTitular || {};
      const segundoTitular = caseData.segundoTitular || {};
      return (
        normalizeSearchText(primerTitular.nombre).includes(searchTerm) ||
        normalizeSearchText(primerTitular.apellidos).includes(searchTerm) ||
        normalizeSearchText(primerTitular.dni).includes(searchTerm) ||
        normalizeSearchText(segundoTitular.nombre).includes(searchTerm) ||
        normalizeSearchText(segundoTitular.apellidos).includes(searchTerm) ||
        normalizeSearchText(caseData.caseNumber).includes(searchTerm)
      );
    });
  }

  if (currentFilters.mobile) {
    const mobileSearch = phoneKey(currentFilters.mobile);
    filteredCases = filteredCases.filter((caseData) => {
      const primerTitular = caseData.primerTitular || {};
      const segundoTitular = caseData.segundoTitular || {};
      return (
        normalizeDigits(primerTitular.movil || '').includes(mobileSearch) ||
        normalizeDigits(segundoTitular.movil || '').includes(mobileSearch) ||
        normalizeDigits(caseData.mobile || '').includes(mobileSearch) ||
        normalizeDigits(caseData.movil || '').includes(mobileSearch)
      );
    });
  }

  if (currentFilters.bankGroup) {
    filteredCases = filteredCases.filter((caseData) => caseData.bankGroup === currentFilters.bankGroup);
  }

  if (currentFilters.operacionesAgent) {
    filteredCases = filteredCases.filter((caseData) =>
      normalizeAgentValue('operacionesAgent', caseData.operacionesAgent) === currentFilters.operacionesAgent
    );
  }

  if (currentFilters.comercialAgent) {
    filteredCases = filteredCases.filter((caseData) =>
      normalizeAgentValue('comercialAgent', caseData.comercialAgent) === currentFilters.comercialAgent
    );
  }

  return filteredCases;
};

// Custom datagrid component
const CustomCofidisDatagrid = () => {
  const listContext = useListContext();

  if (listContext.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!listContext.data || listContext.data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <div>No hay casos Confidis disponibles. Crea el primer caso.</div>
      </Box>
    );
  }

  return (
    <div className="RaDatagrid-root">
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          borderSpacing: 0,
          background: 'var(--ed-card, #ffffff)',
          fontSize: '0.875rem',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <thead>
            <tr style={{
              height: '56px',
              backgroundColor: 'var(--ed-soft, #f8f9fa)',
              borderBottom: '2px solid var(--ed-line, #e9ecef)'
            }}>
              <th style={{
                display: 'none',
                padding: '20px 24px',
                textAlign: 'left',
                borderBottom: '2px solid var(--ed-line, #e9ecef)',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'var(--ed-text-2, #495057)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Número de Caso
              </th>
              <th style={{
                padding: '20px 24px',
                textAlign: 'left',
                borderBottom: '2px solid var(--ed-line, #e9ecef)',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'var(--ed-text-2, #495057)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Primer Titular
              </th>
              <th style={{
                padding: '20px 24px',
                textAlign: 'left',
                borderBottom: '2px solid var(--ed-line, #e9ecef)',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'var(--ed-text-2, #495057)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Segundo Titular
              </th>
              <th style={{
                padding: '20px 24px',
                textAlign: 'left',
                borderBottom: '2px solid var(--ed-line, #e9ecef)',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'var(--ed-text-2, #495057)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Telefono
              </th>
              <th style={{
                display: 'none',
                padding: '20px 24px',
                textAlign: 'left',
                borderBottom: '2px solid var(--ed-line, #e9ecef)',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'var(--ed-text-2, #495057)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                DNI
              </th>
              <th style={{
                display: 'none',
                padding: '20px 24px',
                textAlign: 'left',
                borderBottom: '2px solid var(--ed-line, #e9ecef)',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'var(--ed-text-2, #495057)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Nº Pagas
              </th>
              <th style={{
                display: 'none',
                padding: '20px 24px',
                textAlign: 'left',
                borderBottom: '2px solid var(--ed-line, #e9ecef)',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'var(--ed-text-2, #495057)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Estado
              </th>
              <th style={{
                padding: '20px 24px',
                textAlign: 'left',
                borderBottom: '2px solid var(--ed-line, #e9ecef)',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'var(--ed-text-2, #495057)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Fecha de Registro
              </th>
              <th style={{
                padding: '20px 24px',
                textAlign: 'left',
                borderBottom: '2px solid var(--ed-line, #e9ecef)',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'var(--ed-text-2, #495057)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Operaciones
              </th>
              <th style={{
                padding: '20px 24px',
                textAlign: 'left',
                borderBottom: '2px solid var(--ed-line, #e9ecef)',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'var(--ed-text-2, #495057)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Comercial
              </th>
              <th style={{
                padding: '20px 24px',
                textAlign: 'center',
                borderBottom: '2px solid var(--ed-line, #e9ecef)',
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'var(--ed-text-2, #495057)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {listContext.data.map((record, index) => (
              <tr
                key={record.id || record.uid}
                style={{
                  height: '64px',
                  backgroundColor: index % 2 === 0 ? 'var(--ed-card, #ffffff)' : 'var(--ed-soft, #f8f9fa)',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <td style={{
                  display: 'none',
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--ed-line, #e9ecef)',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: 'var(--ed-text, #212529)'
                }}>
                  {record.caseNumber || '-'}
                </td>
                <td style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--ed-line, #e9ecef)',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: 'var(--ed-text, #212529)'
                }}>
                  <FirstHolderNameField record={record} />
                </td>
                <td style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--ed-line, #e9ecef)',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: 'var(--ed-text, #212529)'
                }}>
                  <SecondHolderNameField record={record} />
                </td>
                <td style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--ed-line, #e9ecef)',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: 'var(--ed-text, #212529)'
                }}>
                  <PhoneField record={record} />
                </td>
                <td style={{
                  display: 'none',
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--ed-line, #e9ecef)',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: 'var(--ed-text, #212529)'
                }}>
                  <NestedTextField source="primerTitular.dni" record={record} />
                </td>
                <td style={{
                  display: 'none',
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--ed-line, #e9ecef)',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: 'var(--ed-text, #212529)'
                }}>
                  <NestedTextField source="primerTitular.numeroPagas" record={record} />
                </td>
                <td style={{
                  display: 'none',
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--ed-line, #e9ecef)',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: 'var(--ed-text, #212529)'
                }}>
                  {record.status || 'En Revisión'}
                </td>
                <td style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--ed-line, #e9ecef)',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: 'var(--ed-text, #212529)'
                }}>
                  {formatDateForDisplay(record.registeredDate)}
                </td>
                <td style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--ed-line, #e9ecef)',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: 'var(--ed-text, #212529)'
                }}>
                  <InlineSelectCell record={record} field="operacionesAgent" caseId={record.id || record.uid} />
                </td>
                <td style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--ed-line, #e9ecef)',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: 'var(--ed-text, #212529)'
                }}>
                  <InlineSelectCell record={record} field="comercialAgent" caseId={record.id || record.uid} />
                </td>
                <td style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid var(--ed-line, #e9ecef)',
                  textAlign: 'center',
                }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<OpenInNewIcon fontSize="small" />}
                    href={buildShowUrl(record.id || record.uid)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                  >
                    Abrir
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const CofidisCaseList = ({ bankGroup = '' }) => {
  const [cofidisCases, setCofidisCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(bankGroup ? { bankGroup } : {});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [total, setTotal] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);

  // Store the cursor needed to start each sequential page.
  const paginationCursors = useRef({});
  const filtersRef = useRef(filters);
  const searchCacheRef = useRef(createEmptySearchCacheState());
  const hasLoadedInitialDataRef = useRef(false);
  const suppressNextPageFetchRef = useRef(false);
  const isMountedRef = useRef(true);
  const fetchRequestIdRef = useRef(0);
  const countRequestIdRef = useRef(0);

  const applyFilters = useCallback((nextFilters) => {
    setFilters(bankGroup ? { ...nextFilters, bankGroup } : nextFilters);
  }, [bankGroup]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Keep filters ref in sync
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const fetchCofidisCases = useCallback(async (targetPage, pageSize, rawFilters, shouldReset = false) => {
    const requestId = ++fetchRequestIdRef.current;
    const currentFilters = normalizeCofidisFilters(rawFilters);
    const isSearching = hasTextSearchFilters(currentFilters);
    const primarySearchTokens = getPrimarySearchTokens(currentFilters);
    const searchCacheKey = JSON.stringify(currentFilters);
    // With the searchPrefixes constraint every fetched doc is a match, so we
    // only need enough to fill one page. Without a token we have to fall back
    // to client-side filtering and need a wider scan window.
    const searchWindowSize = primarySearchTokens.length
      ? Math.max(pageSize + 1, 30)
      : Math.max(pageSize * 4, SEARCH_WINDOW_MIN);
    const listCacheKey = buildCofidisListCacheKey({
      filters: currentFilters,
      page: targetPage,
      perPage: pageSize,
    });
    const shouldForceServer = !isSearching && targetPage > 1;
    const isLatestRequest = () => isMountedRef.current && requestId === fetchRequestIdRef.current;

    try {
      setLoading(true);
      const db = getFirestore();
      const cofidisCasesRef = collection(db, 'cofidisCases');
      const constraints = [orderBy('registeredDate', 'desc')];

      if (currentFilters.bankGroup) {
        constraints.push(where('bankGroup', '==', currentFilters.bankGroup));
      }

      if (currentFilters.operacionesAgent) {
        constraints.push(where('operacionesAgent', '==', currentFilters.operacionesAgent));
      }

      if (currentFilters.comercialAgent) {
        constraints.push(where('comercialAgent', '==', currentFilters.comercialAgent));
      }

      let docs = [];
      let cases = [];

      if (isSearching) {
        const startIndex = (targetPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        if (shouldReset || searchCacheRef.current.key !== searchCacheKey) {
          searchCacheRef.current = {
            ...createEmptySearchCacheState(),
            key: searchCacheKey,
          };
          paginationCursors.current = {};
        }

        while (isLatestRequest()) {
          cases = searchCacheRef.current.cases;
          const filteredCases = filterCofidisCases(cases, currentFilters);
          const hasEnoughCasesForPage = filteredCases.length >= endIndex;

          if (hasEnoughCasesForPage || !searchCacheRef.current.hasMoreServer) {
            if (!isLatestRequest()) {
              return;
            }

            const hasMoreFilteredCases = filteredCases.length > endIndex;
            const hasNextSearchPage = hasMoreFilteredCases || searchCacheRef.current.hasMoreServer;
            const visibleCases = filteredCases.slice(startIndex, endIndex);
            const nextTotal = searchCacheRef.current.hasMoreServer
              ? Math.max(filteredCases.length + 1, endIndex + 1)
              : filteredCases.length;

            setTotal(nextTotal);
            setCofidisCases(visibleCases);
            setHasNextPage(hasNextSearchPage);
            setHasPrevPage(targetPage > 1);
            setLoading(false);
            return;
          }

          // Del VPS; si no contesta, la consulta de Firestore de siempre.
          let fetchedDocs = [];
          await leerCasos({
            filtros: currentFilters,
            tokens: primarySearchTokens,
            limite: searchWindowSize + 1,
            despues: searchCacheRef.current.nextCursor,
            usarEspejo: !bankGroup,
            alRecibir: (ventana) => { fetchedDocs = ventana; },
            firestore: async (despuesSnap, entregar) => {
              const searchConstraints = [...constraints];

              if (primarySearchTokens.length === 1) {
                searchConstraints.push(where('searchPrefixes', 'array-contains', primarySearchTokens[0]));
              } else if (primarySearchTokens.length > 1) {
                searchConstraints.push(where('searchPrefixes', 'array-contains-any', primarySearchTokens));
              }

              if (despuesSnap) {
                searchConstraints.push(startAfter(despuesSnap));
              }

              const searchQuery = query(
                cofidisCasesRef,
                ...searchConstraints,
                limit(searchWindowSize + 1)
              );

              const searchBatchCacheKey = buildCofidisSearchBatchCacheKey({
                filters: currentFilters,
                cursorId: despuesSnap?.id || null,
                windowSize: searchWindowSize,
                token: primarySearchTokens.join('|'),
              });

              let searchSnapshot = null;
              if (isSessionValueFresh(searchBatchCacheKey, LIST_CACHE_MAX_AGE_MS)) {
                try {
                  const cachedBatch = await getDocsFromCache(searchQuery);
                  if (cachedBatch && !cachedBatch.empty) {
                    searchSnapshot = cachedBatch;
                  }
                } catch (err) {
                  searchSnapshot = null;
                }
              }
              if (!searchSnapshot) {
                searchSnapshot = await getDocsFromServer(searchQuery);
                markSessionValueFresh(searchBatchCacheKey);
              }
              entregar(searchSnapshot.docs);
            },
          });

          if (!isLatestRequest()) {
            return;
          }

          const hasMoreSearchResults = fetchedDocs.length > searchWindowSize;
          const docsToAppend = hasMoreSearchResults ? fetchedDocs.slice(0, searchWindowSize) : fetchedDocs;
          const nextCases = docsToAppend.map((leido) => leido.caso);
          const nextCursor = docsToAppend.length > 0
            ? docsToAppend[docsToAppend.length - 1].cursor
            : searchCacheRef.current.nextCursor;

          searchCacheRef.current = {
            key: searchCacheKey,
            cases: mergeCasesById(searchCacheRef.current.cases, nextCases),
            nextCursor,
            hasMoreServer: hasMoreSearchResults && docsToAppend.length > 0,
          };
        }

        return;
      } else {
        let cursor = null;

        if (shouldReset || targetPage === 1) {
          paginationCursors.current = { 1: null };
        } else {
          cursor = paginationCursors.current[targetPage];

          if (!cursor) {
            if (!isLatestRequest()) {
              return;
            }

            setCofidisCases([]);
            setHasNextPage(false);
            setHasPrevPage(targetPage > 1);
            setLoading(false);
            return;
          }
        }

        // `ventana`: [{ caso, cursor }], del VPS o de Firestore.
        const applySnapshot = (ventana) => {
          if (!isLatestRequest()) {
            return;
          }

          docs = ventana;
          cases = docs.map((leido) => leido.caso);

          searchCacheRef.current = createEmptySearchCacheState();

          const hasMore = docs.length > pageSize;
          const docsToShow = hasMore ? docs.slice(0, pageSize) : docs;
          const visibleDocs = docsToShow.map((leido) => leido.caso);
          const nextPage = targetPage + 1;

          Object.keys(paginationCursors.current).forEach((key) => {
            const pageNumber = Number(key);
            if (Number.isInteger(pageNumber) && pageNumber > nextPage) {
              delete paginationCursors.current[pageNumber];
            }
          });

          if (hasMore && docsToShow.length > 0) {
            paginationCursors.current[nextPage] = docsToShow[docsToShow.length - 1].cursor;
          } else {
            delete paginationCursors.current[nextPage];
          }

          if (!isLatestRequest()) {
            return;
          }

          setCofidisCases(visibleDocs);
          setHasNextPage(hasMore);
          setHasPrevPage(targetPage > 1);
        };

        await leerCasos({
          filtros: currentFilters,
          limite: pageSize + 1,
          despues: cursor,
          usarEspejo: !bankGroup,
          alRecibir: applySnapshot,
          firestore: async (despuesSnap, entregar) => {
            const q = despuesSnap
              ? query(cofidisCasesRef, ...constraints, startAfter(despuesSnap), limit(pageSize + 1))
              : query(cofidisCasesRef, ...constraints, limit(pageSize + 1));
            await loadQueryWithCache({
              queryRef: q,
              cacheKey: listCacheKey,
              maxAgeMs: LIST_CACHE_MAX_AGE_MS,
              forceServer: shouldForceServer,
              onCache: (snapshot) => entregar(snapshot.docs),
              onServer: (snapshot) => entregar(snapshot.docs),
            });
          },
        });
      }

      if (isLatestRequest()) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching confidis cases:', error);

      if (!isLatestRequest()) {
        return;
      }

      setLoading(false);
      setCofidisCases([]);
    }
  }, [bankGroup]);

  const fetchTotalCount = useCallback(async (rawFilters) => {
    const requestId = ++countRequestIdRef.current;
    const currentFilters = normalizeCofidisFilters(rawFilters);
    const countCacheKey = buildCofidisCountCacheKey(currentFilters);
    const canUseCountCache = !currentFilters.operacionesAgent && !currentFilters.comercialAgent;
    const cachedCount = canUseCountCache
      ? getFreshSessionValue(countCacheKey, COUNT_CACHE_MAX_AGE_MS)
      : null;
    const isLatestCountRequest = () => isMountedRef.current && requestId === countRequestIdRef.current;

    if (typeof cachedCount === 'number') {
      if (isLatestCountRequest()) {
        setTotal(cachedCount);
      }
      return;
    }

    try {
      const db = getFirestore();
      const cofidisCasesRef = collection(db, 'cofidisCases');
      const constraints = [];

      if (currentFilters.bankGroup) {
        constraints.push(where('bankGroup', '==', currentFilters.bankGroup));
      }

      if (currentFilters.operacionesAgent) {
        constraints.push(where('operacionesAgent', '==', currentFilters.operacionesAgent));
      }

      if (currentFilters.comercialAgent) {
        constraints.push(where('comercialAgent', '==', currentFilters.comercialAgent));
      }

      const countQuery = constraints.length > 0
        ? query(cofidisCasesRef, ...constraints)
        : cofidisCasesRef;

      const nextCount = await contarCasosDelEspejo(currentFilters,
        async () => (await getCountFromServer(countQuery)).data().count,
        !bankGroup);

      if (!isLatestCountRequest()) {
        return;
      }

      setTotal(nextCount);
      if (canUseCountCache) {
        setSessionValue(countCacheKey, nextCount);
      }
    } catch (error) {
      console.warn('Error fetching total count:', error);
    }
  }, [bankGroup]);

  useEffect(() => {
    const loadData = async () => {
      const currentFilters = normalizeCofidisFilters(filters);

      searchCacheRef.current = createEmptySearchCacheState();
      paginationCursors.current = { 1: null };

      if (page !== 1) {
        suppressNextPageFetchRef.current = true;
        setPage(1);
      }

      await fetchCofidisCases(1, perPage, currentFilters, true);

      if (!hasTextSearchFilters(currentFilters)) {
        await fetchTotalCount(currentFilters);
      }

      hasLoadedInitialDataRef.current = true;
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q, filters.mobile, filters.bankGroup, filters.operacionesAgent, filters.comercialAgent, bankGroup]);

  useEffect(() => {
    if (!hasLoadedInitialDataRef.current) {
      return;
    }

    if (suppressNextPageFetchRef.current) {
      suppressNextPageFetchRef.current = false;
      return;
    }

    if (page > 0 && perPage > 0) {
      fetchCofidisCases(page, perPage, filtersRef.current, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
    paginationCursors.current = { 1: null }; // Reset pagination cursors when page size changes
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <ListContextProvider
      value={{
        data: cofidisCases,
        ids: cofidisCases.map(c => c.id || c.uid),
        total: total,
        loading,
        resource: bankGroup ? 'hipotecas' : 'cofidisCases',
        sort: { field: 'registeredDate', order: 'DESC' },
        onSort: () => { },
        currentSort: { field: 'registeredDate', order: 'DESC' },
        displayedFilters: {},
        filterValues: filters,
        hasCreate: !bankGroup,
        page: page,
        perPage: perPage,
        setPage: (_, newPage) => handlePageChange(newPage),
        setPerPage: handlePerPageChange,
        setFilters: applyFilters,
        isLoading: loading,
        selectedIds: [],
        onSelect: () => { },
        onToggleItem: () => { },
        onUnselectItems: () => { },
        refetch: () => fetchCofidisCases(page, perPage, filtersRef.current, false),
        hasBulkActions: false
      }}
    >
      <div className="MuiPaper-root MuiPaper-elevation MuiPaper-rounded MuiPaper-elevation1 MuiCard-root" style={{
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        <div className="MuiToolbar-root MuiToolbar-regular RaListToolbar-toolbar MuiToolbar-gutters" style={{
          display: 'flex',
          alignItems: 'center',
          padding: '20px 24px',
          justifyContent: 'space-between',
          position: 'relative',
          zIndex: 1,
          backgroundColor: 'var(--ed-soft, #f8f9fa)',
          borderBottom: '1px solid var(--ed-line, #e9ecef)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            {!bankGroup && <CreateButton />}
            {/* Sin `exporter` react-admin leia la coleccion entera (2.623
                docs; el proveedor ignora `perPage`), la pagaba y tiraba el
                resultado: `exporter && exporter(data,...)`. Ahora esas
                lecturas producen el CSV que el boton promete. */}
            {!bankGroup && <ExportButton exporter={exportarCasos} />}
            <MemoCofidisCaseFilter
              filters={filters}
              setFilters={applyFilters}
            />
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Per Page</InputLabel>
              <Select
                value={perPage}
                label="Per Page"
                onChange={handlePerPageChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="textSecondary">
              {total > 0 ? `Showing ${(page - 1) * perPage + 1}-${Math.min(page * perPage, total)} of ${total}` : 'No results'}
            </Typography>
          </div>
        </div>
        <div style={{
          marginTop: '0',
          padding: '24px'
        }}>
          <CustomCofidisDatagrid />
        </div>
        {/* Pagination Controls */}
        {total > 0 && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px 24px',
            borderTop: '1px solid var(--ed-line, #e9ecef)',
            gap: 2
          }}>
            <Button
              variant="outlined"
              disabled={!hasPrevPage || loading}
              onClick={() => {
                if (page > 1) {
                  setPage(page - 1);
                }
              }}
            >
              Previous
            </Button>
            <Typography variant="body2" color="textSecondary">
              Page {page} of {totalPages}
            </Typography>
            <Button
              variant="outlined"
              disabled={!hasNextPage || loading}
              onClick={() => {
                if (hasNextPage) {
                  handlePageChange(page + 1);
                }
              }}
            >
              Next
            </Button>
          </Box>
        )}
      </div>
    </ListContextProvider>
  );
}; 
